/* =============================================================================
   agb-birthday-overlay.js - the birthday band under the announcement bar

   WHAT: a celebration strip mounted right under the announcement bar on every
   page: a drifting colour wash with confetti falling through it, bunting,
   and a garland of balloons floating up that SPELL the greeting, one letter
   printed on each in the storefront's heading face. There is no headline
   text: the balloons are the words. Move the pointer over the band and they
   shy away from it; a click, or a tap on a phone, pops the one under the
   pointer - nothing pops on its own, and a sliding finger only pushes.
   Balloons come back, so it never runs dry. The header lockup is swapped for
   the birthday patch and the shipping bar joins in while the band is up.

   There is no rifle here. The shooting range that grew out of this band lives
   in agb-turkey-layout.js and only runs on preview themes.

   TRIGGER: localStorage.birthday_overlay === 'true'   (anything else = off)
     - also armable from a link:  ?birthday_overlay=true   (or =false to disarm)
       The parameter is written to localStorage and stripped from the URL, so a
       Drip email or the /pages/birthday page can switch it on with a plain link.
     - other tabs follow along (storage event), no reload needed.

   KILL SWITCH: window.AGB_BIRTHDAY_OFF = true

   ISOLATION: own file, injects its own CSS, inserts ONE element, edits no theme
   code. Pointer handling is confined to the band - the rest of the page shops
   exactly as before. It announces itself for anyone who wants to listen:
     document event  agb:bday-pop         {count}          every balloon popped
     document event  agb:bday-all-popped  {count, total}   EVERY time the sky is
                     emptied -- balloons come back, so it can be cleared again, and
                     each clearing earns the reward. What the Games loader listens
                     for, to open a popup set to this signal
     window.AGB_BirthdayOverlay = { show, hide, armed, popped, total, allPopped }

   MOUNT: after the announcement bar's section wrapper; if there is none, before
   the header section; failing that, at the top of <body>.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'birthday_overlay';
  // THREE SETTINGS, NOT TWO (Dorian, 2026-09-17: "make a 3 position button
  // that switches between regular pop - music pop - mute"):
  //   'music' - the balloon pops AND leaves its note, so clearing the line
  //             plays Happy Birthday. This is what the band did before, so it
  //             stays the default and nobody's sound changes under them.
  //   'pop'   - the burst only. The tune is a lot on a shop page; this is the
  //             way to keep the balloons alive without it.
  //   'off'   - silent.
  // The old values still read correctly: 'off' is still off, and 'on' - what
  // anyone who muted and unmuted before today has stored - means 'music'.
  var SOUND_KEY = 'birthday_overlay_sound';
  var MODES = ['music', 'pop', 'off'];

  if (window.AGB_BIRTHDAY_OFF) return;
  if (window.__agbBirthdayOverlay) return;      // loaded twice? once is enough
  window.__agbBirthdayOverlay = true;

  // STRIP THE URL ONLY AFTER THE GEO-REDIRECT HAS HAD ITS SAY (2026-09-22).
  // EasyLocation (async, in <head>) sends an Israeli visitor from agilitegear.com
  // to agilite.co.il -- and it builds that target from window.location.href AT
  // THE MOMENT ITS GEO LOOKUP ANSWERS. Measured on a warm load: the lookup
  // answered at 644 ms and this file ran at 645 ms. Whichever won the coin flip
  // decided whether the redirected URL still carried the arming parameters; if
  // the strip won, the IL page received the address (bde) and no band. The app
  // deletes nothing we need (only a parameter literally named `email`, which
  // theme.liquid has already copied into `bde`), so all that was ever lost was
  // what WE removed first. Decisions stay immediate; only the address-bar
  // clean-up waits -- until the app has written its decided-location cookie
  // (it prefixes every cookie with `el-`), or 6 s (its lookup times out at 5).
  // "Settled" is the app's OWN word for it: it dispatches
  // `easylocation_data_loaded` on document when its lookup has answered (the
  // cookie it reads, geoip-location, is written by its server and is absent
  // on a same-store visit, so it is only a second signal here). If instead it
  // announces `easylocation_redirect_after_check`, the page is about to leave
  // -- it navigates 5 ms later -- and the clean-up is skipped altogether,
  // because the one thing that must not happen now is a strip racing that
  // navigation. Either way the flag is already written and the band is up.
  function afterRedirectSettled(fn) {
    var started = Date.now(), settled = false, leaving = false;
    try {
      document.addEventListener('easylocation_data_loaded', function () { settled = true; });
      document.addEventListener('easylocation_redirect_after_check', function () { leaving = true; });
      settled = settled || /(^|;\s*)el-geoip-location=/.test(document.cookie || '');
    } catch (e) { /* no document (a harness): the timer alone decides */ }
    (function tick() {
      if (leaving) return;
      if (settled || Date.now() - started > 6000) { try { fn(); } catch (e) {} return; }
      setTimeout(tick, 250);
    })();
  }

  // ---------------------------------------------------------------- arming --
  // A link can switch the band on or off: the value lands in localStorage and
  // the parameter leaves the URL, so a shared link never re-arms a browser that
  // has since turned it off.
  try {
    var qs = new URLSearchParams(window.location.search);
    if (qs.has(KEY)) {
      localStorage.setItem(KEY, qs.get(KEY) === 'true' ? 'true' : 'false');
      // The flag is written NOW; the parameter leaves the address bar only once
      // the geo-redirect has decided -- see afterRedirectSettled(). Stripping
      // it at once was half of how a redirected visitor arrived with no band.
      afterRedirectSettled(function () {
        var now = new URLSearchParams(window.location.search);
        if (!now.has(KEY)) return;
        now.delete(KEY);
        window.history.replaceState(null, '', window.location.pathname +
          (now.toString() ? '?' + now.toString() : '') + window.location.hash);
      });
    }
  } catch (e) { /* storage or history unavailable - stay quiet */ }

  function armed() {
    try { return localStorage.getItem(KEY) === 'true'; } catch (e) { return false; }
  }
  function soundMode() {
    var v;
    try { v = localStorage.getItem(SOUND_KEY); } catch (e) { return 'music'; }
    if (v === 'off') return 'off';
    if (v === 'pop') return 'pop';
    return 'music';                       // null, 'on', or anything unknown
  }
  function soundOn() { return soundMode() !== 'off'; }

  // Brand palette (oxblood, olive, sand, bone, charcoal) - balloons and bunting.
  var COLORS = ['#8f1814', '#92ae74', '#c9b79c', '#f2f2f2', '#3d3d3d', '#b3462f'];

  // Sibling assets (the patch that replaces the logo) live next to this file on
  // the theme CDN; the URL is derived from ours and keeps our ?v= so a new push
  // of the script means a fresh fetch of the image too.
  var SCRIPT_URL = (function () {
    var s = document.currentScript && document.currentScript.src;
    if (!s) {
      var all = document.querySelectorAll('script[src*="agb-birthday-overlay"]');
      s = all.length ? all[all.length - 1].src : '';
    }
    return s || '';
  })();
  function siblingAsset(name) {
    if (!SCRIPT_URL) return name;
    return SCRIPT_URL.replace(/agb-birthday-overlay\.js(?=\?|$)/, name);
  }
  var LOGO_URL = siblingAsset('agb-birthday-logo.webp');

  // How far the target's presence is felt (px) and how far a balloon will dodge.
  var REPEL_RADIUS = 110, REPEL_MAX = 46;
  // How far each row above the band floor is lifted. Sized so an upper row's
  // bodies clear the row below, leaving only the strings to overlap - which
  // is what a real garland does.
  var ROW_STEP = 38;

  // ------------------------------------------------------------------- css --
  var CSS = [
    /* the band: a slow-moving colour wash in the brand palette, confetti
       falling through it, and a soft vignette so the words stay readable */
    '.agb-bday{position:relative;overflow:hidden;height:200px;color:#f2f2f2;',
    '  background:linear-gradient(115deg,#4a0f0d,#8f1814,#b3462f,#c9b79c,#92ae74,#3f5233,#4a0f0d);',
    '  background-size:320% 320%;animation:agb-bday-wash 16s ease-in-out infinite;',
    '  user-select:none;-webkit-user-select:none;touch-action:pan-y pinch-zoom;',
    '  font-family:inherit;box-shadow:inset 0 -1px 0 rgba(255,255,255,.12)}',
    '.agb-bday.is-lock{cursor:pointer}',
    '.agb-bday::before{content:"";position:absolute;inset:0 0 -40px;pointer-events:none;opacity:.9;',
    '  background-image:',
    '    radial-gradient(circle,#ffd23f 0 2px,transparent 3px),',
    '    radial-gradient(circle,#f2f2f2 0 1.5px,transparent 2.5px),',
    '    radial-gradient(circle,#ff6b6b 0 2px,transparent 3px),',
    '    radial-gradient(circle,#7ed6ff 0 1.5px,transparent 2.5px),',
    '    radial-gradient(circle,#c9b79c 0 2px,transparent 3px);',
    '  background-size:140px 120px,90px 160px,170px 140px,110px 130px,200px 180px;',
    '  background-position:0 0,30px 60px,80px 20px,10px 90px,120px 40px;',
    '  animation:agb-bday-confetti 9s linear infinite}',
    '.agb-bday::after{content:"";position:absolute;inset:0;pointer-events:none;',
    '  background:radial-gradient(ellipse at 50% 45%,transparent 35%,rgba(0,0,0,.42) 100%)}',
    '@media (max-width:749px){.agb-bday{height:140px}}',
    '.agb-bday *{box-sizing:border-box}',
    /* the logo swap: the theme's lockup steps aside for the birthday patch */
    '.agb-bday-logo-on .header__logo-image{display:none!important}',
    '.agb-bday-logo-on{height:auto!important;overflow:visible!important}',
    '.agb-bday-logo{display:block;width:auto;max-width:100%;margin:0 auto}',
    /* Bunting: pennants drawn as ONE svg tile - the string line and two
       hanging triangles, oxblood and olive. The old version stacked two
       diagonal half-fills per tile and a second offset row on top of that,
       which never made a triangle: at phone size it read as a row of arrows
       (Dorian, 2026-09-16: "on mobile the triangles look weird"). An svg
       scales cleanly, so the phone just gets a smaller tile. */
    '.agb-bday__bunting{position:absolute;left:0;right:0;top:0;height:20px;pointer-events:none;opacity:.92;',
    '  background:url(data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%2248%22%20height=%2220%22%3E%3Cpath%20d=%22M0,0h48v1H0z%22%20fill=%22%23ffffff%22%20fill-opacity=%22.45%22/%3E%3Cpath%20d=%22M2,1h20l-10,17z%22%20fill=%22%238f1814%22/%3E%3Cpath%20d=%22M26,1h20l-10,17z%22%20fill=%22%2392ae74%22/%3E%3C/svg%3E) 0 0/48px 20px repeat-x}',
    '@media (max-width:749px){.agb-bday__bunting{height:15px;background-size:36px 15px}}',
    /* There is no headline element: the balloons ARE the words (Dorian,
       2026-09-16: "remove the text, the balloons are enough"). The letters
       carry the storefront's heading face - see .agb-bday__letter below */
    /* balloons: the outer box floats (CSS animation), the inner box dodges the
       target (a transition on a transform of its own) */
    '.agb-bday__sky{position:absolute;inset:0;pointer-events:none}',
    '.agb-bday__balloon{position:absolute;left:var(--x);bottom:calc(-6px + var(--lift,0px));width:var(--w);',
    '  animation:agb-bday-float var(--dur) ease-in-out var(--delay) infinite alternate;will-change:transform;',
    '  transition:translate 2.4s cubic-bezier(.22,.7,.3,1)}',
    /* a replacement balloon is parked just under the band floor - the band
       clips it, so it is out of sight - and released on the next frame, so it
       rises into the sky instead of appearing out of nowhere. "translate" is
       its own property, so the parking never fights the float animation on
       "transform". Distance = the balloon's own height (body + string) + a
       margin, so every size clears the floor */
    '.agb-bday__balloon.is-rising{translate:0 calc(var(--w) * 1.25 + 46px);transition:none}',
    '.agb-bday__inner{transform:translate(var(--rx,0px),var(--ry,0px));transition:transform .28s cubic-bezier(.2,.8,.3,1)}',
    '.agb-bday__body{position:relative;width:var(--w);height:calc(var(--w) * 1.22);',
    '  border-radius:50% 50% 50% 50% / 44% 44% 56% 56%;',
    '  background:radial-gradient(circle at 32% 28%,rgba(255,255,255,.55) 0,rgba(255,255,255,.12) 22%,transparent 40%),var(--c);',
    '  box-shadow:inset -6px -8px 12px rgba(0,0,0,.28)}',
    '.agb-bday__body::after{content:"";position:absolute;left:50%;bottom:-6px;width:0;height:0;transform:translateX(-50%);',
    '  border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid var(--c);filter:brightness(.85)}',
    '.agb-bday__string{width:1px;height:min(22px,calc(var(--w) * .9));margin:8px auto 0;background:rgba(255,255,255,.45);',
    '  transform-origin:top;animation:agb-bday-sway calc(var(--dur) * .6) ease-in-out var(--delay) infinite alternate}',
    '.agb-bday__balloon.is-popping{animation:none}',
    '.agb-bday__balloon.is-popping .agb-bday__body{animation:agb-bday-pop .32s ease-out forwards}',
    '.agb-bday__balloon.is-popping .agb-bday__string{animation:agb-bday-fall .45s ease-in forwards}',
    '.agb-bday__bit{position:absolute;width:7px;height:7px;border-radius:2px;pointer-events:none;',
    '  animation:agb-bday-bit .7s cubic-bezier(.2,.7,.3,1) forwards}',
    /* one letter printed on each balloon, so the line spells the greeting.
       The ink is picked per balloon (light latex takes dark print, dark latex
       takes white) and it rides inside the body, so it floats, sways, dodges
       and pops with the balloon. A floor on the size keeps the phone's small
       balloons readable */
    '.agb-bday__letter{position:absolute;left:50%;top:46%;z-index:1;pointer-events:none;',
    '  transform:translate(-50%,-50%);',
    '  font-family:acumin-pro,"Agilite Display",Cabin,"Arial Black",sans-serif;font-weight:700;',
    '  font-size:max(10px,calc(var(--w) * .56));line-height:1;letter-spacing:.02em;',
    '  color:var(--ink,rgba(255,255,255,.94))}',
    /* controls */
    '.agb-bday__score{position:absolute;right:14px;bottom:12px;font-size:11px;letter-spacing:.2em;text-transform:uppercase;',
    '  color:#c9b79c;pointer-events:none}',
    '.agb-bday__btn{position:absolute;top:28px;width:30px;height:30px;border:1px solid rgba(255,255,255,.22);border-radius:2px;',
    '  background:rgba(0,0,0,.35);color:#f2f2f2;font:600 13px/1 inherit;cursor:pointer;display:flex;align-items:center;justify-content:center}',
    '.agb-bday__btn:hover{background:rgba(143,24,20,.85);border-color:#8f1814}',
    '.agb-bday__close{right:12px}',
    '.agb-bday__sound{right:48px}',
    '.agb-bday__sound.is-pop{font-size:15px}',
    /* off is the note struck through, not merely a fainter note: a shopper
       cannot tell two opacities apart without the other one beside it */
    '.agb-bday__sound.is-off{opacity:.55}',
    '.agb-bday__sound.is-off::after{content:"";position:absolute;left:5px;right:5px;top:50%;',
    '  height:1px;background:currentColor;transform:rotate(-45deg);transform-origin:center}',
    '@media (max-width:749px){.agb-bday__btn{top:26px}.agb-bday__score{display:none}}',
    /* the shipping bar above joins the party: colour cycling, a light sweep, a cake */
    '.agb-bday-banner{background:linear-gradient(90deg,#8f1814,#b3462f,#c9b79c,#92ae74,#3f5233,#8f1814)!important;',
    '  background-size:300% 100%!important;animation:agb-bday-shift 7s linear infinite;position:relative;overflow:hidden}',
    '.agb-bday-banner,.agb-bday-banner *{color:#fff!important;text-shadow:0 1px 0 rgba(0,0,0,.35)}',
    '.agb-bday-banner::after{content:"";position:absolute;top:0;bottom:0;left:-40%;width:35%;pointer-events:none;',
    '  background:linear-gradient(100deg,transparent,rgba(255,255,255,.35),transparent);animation:agb-bday-sweep 4s ease-in-out infinite}',
    '.agb-bday-banner .announcement-bar__carousel p::before{content:"\\1F382\\00A0\\00A0"}',
    '.agb-bday-banner .announcement-bar__carousel p::after{content:"\\00A0\\00A0\\1F388"}',
    /* motion */
    '@keyframes agb-bday-wash{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}',
    /* falling: every layer travels DOWN exactly two of its own tiles
       (120/160/140/130/180px high), so the loop has no seam */
    '@keyframes agb-bday-confetti{to{background-position:0 240px,30px 380px,80px 300px,10px 350px,120px 400px}}',
    '@keyframes agb-bday-shift{to{background-position:300% 0}}',
    '@keyframes agb-bday-sweep{0%,60%{left:-40%}100%{left:110%}}',
    '@keyframes agb-bday-float{from{transform:translate3d(0,0,0)}to{transform:translate3d(var(--sway),calc(var(--rise) * -1),0)}}',
    '@keyframes agb-bday-sway{from{transform:rotate(-5deg)}to{transform:rotate(5deg)}}',
    '@keyframes agb-bday-pop{0%{transform:scale(1);opacity:1}45%{transform:scale(1.35);opacity:.9}100%{transform:scale(1.6);opacity:0}}',
    '@keyframes agb-bday-fall{to{transform:translateY(60px) rotate(25deg);opacity:0}}',
    '@keyframes agb-bday-bit{0%{transform:translate(0,0) rotate(0);opacity:1}100%{transform:translate(var(--dx),var(--dy)) rotate(540deg);opacity:0}}',
    '@media (prefers-reduced-motion:reduce){.agb-bday__balloon,.agb-bday__string{animation:none}',
    '  .agb-bday__balloon{bottom:14px;transition:none}.agb-bday__balloon.is-rising{translate:none}',
    '  .agb-bday__inner{transition:none}}'
  ].join('\n');

  // --------------------------------------------------------------- helpers --
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function rnd(a, b) { return a + Math.random() * (b - a); }
  function pick(list) { return list[Math.floor(Math.random() * list.length)]; }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  function isSticky(n) {
    try { return getComputedStyle(n).position === 'sticky'; } catch (e) { return false; }
  }

  // The band belongs INSIDE the announcement bar's section, not after it.
  // Same place on the page either way - but the desktop product page hides
  // the chrome by sliding [id$="__announcement-bar"] and the header up by
  // their COMBINED HEIGHT and pulling the product section up by the same
  // amount (agmf-desktop-header.js). A band sitting between those two is in
  // neither measurement: the header slid down onto the middle of the band and
  // the page came up over its lower edge, cutting the band in three (Dorian,
  // 2026-09-16: "the banner is moving out of visibility when starting
  // scrolling, look what it does"). Inside the section the band is part of
  // that height and travels with it. A STICKY bar is left alone - the band
  // would be pinned to the viewport, which is worse than the cut.
  function mountPoint() {
    var bar = document.querySelector('[data-announcement-bar], .announcement-bar');
    var section = bar && bar.closest('.shopify-section');
    if (section && !isSticky(section)) return { node: section, where: 'beforeend' };
    var wrap = section || bar;
    if (wrap) return { node: wrap, where: 'afterend' };
    var header = document.querySelector('.shopify-section .header, header.header, #shopify-section-header');
    var hw = header && (header.closest('.shopify-section') || header);
    if (hw) return { node: hw, where: 'beforebegin' };
    return { node: document.body, where: 'afterbegin' };
  }

  // ---------------------------------------------------------------- sound --
  var audio = null;
  function ctx() {
    if (!soundOn()) return null;
    try {
      if (!audio) audio = new (window.AudioContext || window.webkitAudioContext)();
      if (audio.state === 'suspended') audio.resume();
      return audio;
    } catch (e) { return null; }
  }
  // THE POPS PLAY HAPPY BIRTHDAY. Each balloon takes the next note and the tune
  // starts again when it runs out, so a shopper clearing the sky plays the song
  // rather than hearing the same blip fourteen times (Dorian, 2026-09-17).
  // A NOTE BELONGS TO A PLACE, NOT TO A TURN (Dorian, 2026-09-17: "each ballon
  // according to its place"). Balloon n in the line always plays note n, so the
  // same balloon sounds the same every time - including after it comes back -
  // and popping the line left to right plays the song. A line of fourteen
  // therefore reaches the first fourteen notes; the rest are there for a longer
  // line. Change the tune by changing this one array; names are looked up below.
  var TUNE = [
    'G4', 'G4', 'A4', 'G4', 'C5', 'B4',              // Hap-py birth-day to you
    'G4', 'G4', 'A4', 'G4', 'D5', 'C5',              // Hap-py birth-day to you
    'G4', 'G4', 'G5', 'E5', 'C5', 'B4', 'A4',        // Hap-py birth-day dear ...
    'F5', 'F5', 'E5', 'C5', 'D5', 'C5'               // Hap-py birth-day to you
  ];
  var NOTE_HZ = {
    C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88,
    C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00, B5: 987.77,
    C6: 1046.50
  };

  // TWO SOUNDS AT ONCE: the balloon still pops, and the note it was carrying
  // rings out behind the burst (Dorian, 2026-09-17: "but with poping sound").
  //
  // NOT OSCILLATORS (Dorian, 2026-09-17: "the sound is too electronic"). Both
  // halves used to be `triangle` oscillators - the burst a 880->140Hz sweep,
  // the note a bare triangle held for 400ms. A triangle is a pure tone, and a
  // swept pure tone is the sound of a synthesiser; a sustained one is the
  // sound of an organ. Neither is a balloon. So each half is now made the way
  // the real thing makes it:
  //
  //   the burst  - a balloon bursting is BROADBAND NOISE, not a pitch. A short
  //                noise buffer, shaped by a band-pass so it has a body rather
  //                than a hiss, over in a tenth of a second.
  //   the note   - a struck bar, not a held tone: three sine partials, each
  //                quieter and shorter than the one below it, so the sound has
  //                a bright edge that falls away into the fundamental. That
  //                decay is what the ear reads as something being struck.
  //
  // The tune, the per-place note and the sound gate above are untouched.
  var noiseBuf = null;
  function noise(c) {
    if (noiseBuf && noiseBuf.sampleRate === c.sampleRate) return noiseBuf;
    var n = Math.floor(c.sampleRate * 0.12);
    var b = c.createBuffer(1, n, c.sampleRate);
    var d = b.getChannelData(0);
    // pre-shaped so the burst is over almost at once however it is played
    for (var i = 0; i < n; i++) {
      var k = 1 - i / n;
      d[i] = (Math.random() * 2 - 1) * k * k * k;
    }
    noiseBuf = b;
    return b;
  }

  function popSound(place) {
    var c = ctx(); if (!c) return;
    var t = c.currentTime;

    // 1. the burst - filtered noise, with a low thump under it for the body
    var src = c.createBufferSource(); src.buffer = noise(c);
    var bp = c.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.setValueAtTime(1500, t); bp.Q.value = 0.7;
    var ng = c.createGain();
    ng.gain.setValueAtTime(0.28, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
    src.connect(bp); bp.connect(ng); ng.connect(c.destination);
    src.start(t); src.stop(t + 0.12);

    var body = c.createOscillator(); body.type = 'sine';
    body.frequency.setValueAtTime(170, t);
    body.frequency.exponentialRampToValueAtTime(70, t + 0.07);
    var bg = c.createGain();
    bg.gain.setValueAtTime(0.16, t);
    bg.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    body.connect(bg); bg.connect(c.destination); body.start(t); body.stop(t + 0.09);

    // 2. the note it leaves behind - this is what carries the tune, and it is
    //    the half the button turns off: 'pop' keeps the burst above and stops
    //    here, so the balloons still sound without playing the song.
    if (soundMode() !== 'music') return;
    var hz = NOTE_HZ[TUNE[(place || 0) % TUNE.length]] || 523.25;
    var tone = c.createGain();
    tone.gain.setValueAtTime(1, t);
    tone.connect(c.destination);
    // partial, its share of the level, and how long it lasts
    var PARTIALS = [[1, 0.24, 0.62], [2, 0.07, 0.34], [3.01, 0.03, 0.20]];
    for (var p = 0; p < PARTIALS.length; p++) {
      var mult = PARTIALS[p][0], lvl = PARTIALS[p][1], life = PARTIALS[p][2];
      var o = c.createOscillator(); o.type = 'sine';
      o.frequency.setValueAtTime(hz * mult, t);
      var g = c.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(lvl, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + life);
      o.connect(g); g.connect(tone);
      o.start(t); o.stop(t + life + 0.02);
    }
  }

  // ----------------------------------------------------------------- band --
  var band = null, sky = null, scoreEl = null;
  var popped = 0, aimRaf = 0, px = 0, py = 0;
  // Fired once per arming: the all-popped signal is a moment, not a state.
  var allPopped = false;
  // HOW LONG THE BAND STAYS AS IT WAS LEFT, counted from the LAST pop rather
  // than from each balloon's own. That is the part to keep: with fourteen
  // letters a per-balloon timer had the first one back before the last was
  // gone, so the sky never emptied and the popup this band exists to fire
  // never fired. Waiting for quiet instead means any pace clears it.
  //
  // The LENGTH though only has to outlast the gap between two pops, not the
  // whole visit. At twelve seconds the band sat empty long after the shopper
  // had finished (Dorian, 2026-09-17: "the ballons takes too long to come
  // back"). Three and a half is still far longer than anyone's click gap, so
  // nothing comes back mid-run, and the sky refills almost as soon as they
  // stop.
  // HOW LONG THE BAND STAYS AS IT WAS LEFT, counted from the LAST pop. Safe to
  // lengthen: popping every letter counts even if some came back while somebody
  // was aiming, so this no longer decides whether anybody gets their code -- it
  // is only how the band feels. (Dorian, 2026-09-17: "coming back too fast".)
  var RETURN_MS = 8000;

  // The balloons that have been popped and are waiting to come back, and the
  // one timer they share. Restarted by every pop: while somebody is still
  // popping, nothing returns.
  var waiting = [];
  var returnTimer = null;

  // How long between one balloon arriving and the next when the band fills at
  // load. Fourteen letters at this pace put the whole phrase up in about two
  // and a half seconds -- long enough to read as an arrival, short enough that
  // nobody waits for it.
  var ARRIVE_STEP_MS = 150;

  // ONE LETTER PER BALLOON: the balloons spell the greeting across the band
  // the way a party garland does, exclamation mark and all (Dorian,
  // 2026-09-16: "each balloon is a letter", "with the ! too", "the full happy
  // birthday! both mobile and desktop"). The whole phrase on every screen, so
  // the phone fits all fourteen by using more of the width and by capping each
  // balloon to its share of the line. The space is a GAP in the line rather
  // than a balloon - which is why where a balloon sits (letters[i].at) is not
  // simply its index.
  // The phone drops the exclamation mark and takes smaller balloons: fifteen
  // of them across a 375px band left no air at all (Dorian, 2026-09-16: "on
  // the mobile you can make the balloons smaller, maybe without ! etc cuz
  // its too crowded now").
  // ONE LINE ON A DESKTOP, TWO ON A PHONE. Fourteen letters across 375px left
  // each balloon 17px wide with a 10px letter on it - legible only just, and
  // the row read as a smudge (Dorian, 2026-09-17: "in mobile ... find a way so
  // they would take less space, or 2 rows"). Split over two rows the longest
  // row is nine, so every balloon is half as wide again, the letters go from
  // 10px to 15px, and the exclamation mark comes back. Top row first.
  function phraseRows() {
    return window.innerWidth < 750 ? ['HAPPY', 'BIRTHDAY!'] : ['HAPPY BIRTHDAY!'];
  }
  var letters = [];      // one per balloon: { ch, at, row } - at = 0..1 across the band
  var rowCount = 1;
  var slotPx = 60;       // one character's worth of room, taken from the BUSIEST row
  var repelMax = REPEL_MAX;
  function planLetters() {
    var rows = phraseRows();
    rowCount = rows.length;
    letters = [];
    var busiest = 1, r, i, chars;
    for (r = 0; r < rows.length; r++) if (rows[r].length > busiest) busiest = rows[r].length;
    // EVERY ROW USES THE SAME SPACING, and a short row is centred over a long
    // one rather than stretched to the same width. Stretching gave "HAPPY" 40px
    // between letters over a "BIRTHDAY!" with 10px, and pushed its last balloon
    // under the sound and close buttons in the corner.
    for (r = 0; r < rows.length; r++) {
      chars = rows[r].split('');
      var indent = (1 - chars.length / busiest) / 2;
      for (i = 0; i < chars.length; i++) {
        if (chars[i] === ' ') continue;
        letters.push({ ch: chars[i], at: indent + (i + 0.5) / busiest, row: r });
      }
    }
    // One character's worth of room governs everything that could scramble the
    // sentence: the balloon's size, how far it sways, how far it dodges. Taken
    // from the BUSIEST row so both rows carry the same size - a short row of
    // fat balloons over a long row of thin ones reads as two different things.
    var span = window.innerWidth < 750 ? 92 : 84;
    slotPx = span / 100 * window.innerWidth / busiest;
    repelMax = Math.max(10, Math.min(REPEL_MAX, Math.round(slotPx * 0.7)));
    return letters.length;
  }
  function balloonCount() { return planLetters(); }

  // Ink that can be read on THIS balloon: latex this light takes dark print,
  // anything darker takes white. (Rec. 601 luma - enough for six colours.)
  function inkFor(hex) {
    var c = String(hex).trim().replace('#', '');
    if (c.length !== 6) return 'rgba(255,255,255,.94)';
    var r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? 'rgba(26,26,26,.78)' : 'rgba(255,255,255,.94)';
  }

  function makeBalloon(i, total) {
    var b = el('div', 'agb-bday__balloon');
    var small = window.innerWidth < 750;
    var w = Math.round(small ? rnd(30, 42) : rnd(38, 58));
    // Its place in the line, and a width that cannot swallow its neighbours:
    // fourteen balloons on a phone only fit because each one is held to its
    // share of the line. The jitter is small on purpose - the line has to
    // stay readable as a sentence.
    var spot = letters[i] || { ch: '', at: (i + 0.5) / Math.max(1, total), row: 0 };
    var span = small ? 92 : 84;                            // % of the band the line uses
    var room = Math.floor(slotPx * (small ? 0.70 : 0.82));
    if (room > 10 && room < w) w = Math.max(15, room);
    var x = small ? 4 + spot.at * span : 6 + spot.at * span;
    var colour = pick(COLORS);
    // The nudge off the grid is a share of the air, never more than it - and
    // CAPPED, because this converts a pixel share into a percentage by dividing
    // by the viewport. Caught 2026-09-17 on the IL port: when innerWidth reads 0
    // (a reflow, a hidden frame, an emulated viewport mid-change) the guard
    // divides by 1 instead, the nudge becomes hundreds of percent, and every
    // balloon lands thousands of pixels off-screen. Two percent is already more
    // than the effect ever wants.
    var vw = window.innerWidth || 0;
    var jitter = vw > 0 ? Math.min(2, Math.abs(slotPx - w) * 0.12 / vw * 100) : 0;
    b.style.setProperty('--x', (x + rnd(-jitter, jitter)).toFixed(2) + '%');
    b.style.setProperty('--w', w + 'px');
    b.style.setProperty('--c', colour);
    // Which row it hangs on. Rows are listed top first, so the LAST one sits on
    // the band floor and every row above it is lifted clear.
    var lift = (rowCount - 1 - (spot.row || 0)) * ROW_STEP;
    if (lift) b.style.setProperty('--lift', lift + 'px');
    b.style.setProperty('--dur', rnd(5, 9).toFixed(2) + 's');
    b.style.setProperty('--delay', (-rnd(0, 8)).toFixed(2) + 's');
    // A modest bob: the balloons are the headline now, and a wide float
    // scattered the letters up and down until the phrase was hard to read.
    // Stacked rows bob less still - the top row has the bunting just above it.
    var rise = rowCount > 1 ? rnd(6, 14) : (small ? rnd(10, 24) : rnd(16, 34));
    b.style.setProperty('--rise', Math.round(rise) + 'px');
    // Drift sideways only into the air it actually has: on a phone the gap
    // between letters is a few px, and a sway wider than that had
    // neighbours touching at the ends of their travel.
    var swayMax = Math.max(2, Math.min(18, Math.round((slotPx - w) / 2 * 0.7)));
    b.style.setProperty('--sway', Math.round(rnd(-swayMax, swayMax)) + 'px');
    var inner = el('div', 'agb-bday__inner');
    var body = el('div', 'agb-bday__body');
    if (spot.ch) {
      var letter = el('span', 'agb-bday__letter', spot.ch);
      letter.style.setProperty('--ink', inkFor(colour));
      body.appendChild(letter);
    }
    inner.appendChild(body);
    inner.appendChild(el('div', 'agb-bday__string'));
    b.appendChild(inner);
    b._slot = i; b._total = total;
    return b;
  }

  function burst(x, y, color) {
    for (var i = 0; i < 10; i++) {
      var bit = el('span', 'agb-bday__bit');
      var a = rnd(0, Math.PI * 2), dist = rnd(24, 70);
      bit.style.left = x + 'px'; bit.style.top = y + 'px';
      bit.style.background = i % 3 === 0 ? pick(COLORS) : color;
      bit.style.setProperty('--dx', Math.round(Math.cos(a) * dist) + 'px');
      bit.style.setProperty('--dy', Math.round(Math.sin(a) * dist + 20) + 'px');
      sky.appendChild(bit);
      setTimeout(function (n) { n.remove(); }.bind(null, bit), 800);
    }
  }

  // WHICH LETTERS HAVE BEEN POPPED SINCE THE LAST REWARD. Cleared when the
  // reward fires, so clearing the band stays a moment that recurs.
  var poppedSlots = {};
  var poppedSlotCount = 0;

  function markPopped(slot) {
    var key = String(slot);
    if (poppedSlots[key]) return;
    poppedSlots[key] = true;
    poppedSlotCount += 1;
  }

  // THE BAND HAS BEEN CLEARED - AND IT CAN BE CLEARED AGAIN.
  //
  // TWO WAYS TO HAVE CLEARED IT, and the second is the one a phone needs:
  //   * nothing is left in the sky right now; or
  //   * every letter has been popped since the last reward, even if some have
  //     come back in the meantime.
  //
  // The second exists because the refill waits for quiet, and quiet is measured
  // in POPS. On a desktop a hand over the band still moves the pointer; on a
  // phone there is NOTHING between one tap and the next, so looking for the
  // next balloon is indistinguishable from walking away. Dorian popped ten,
  // paused four seconds to find the rest, and got all fourteen back. A balloon
  // returning is the band refilling itself -- it is not the shopper un-popping
  // it, and it must not take the work away.
  function checkSky(total) {
    if (!sky) return;
    var empty = !sky.querySelector('.agb-bday__balloon:not(.is-popping)');
    var cleared = empty || (total > 0 && poppedSlotCount >= total);
    if (cleared === allPopped) return;
    allPopped = cleared;
    if (cleared) {
      document.dispatchEvent(new CustomEvent('agb:bday-all-popped', { detail: { count: popped, total: total } }));
      // Earned again from here: the next run starts from nothing popped.
      poppedSlots = {};
      poppedSlotCount = 0;
    }
  }

  function popBalloon(b, x, y) {
    if (b.classList.contains('is-popping')) return;
    b.classList.add('is-popping');
    burst(x, y, b.style.getPropertyValue('--c'));
    popSound(b._slot);
    popped += 1;
    scoreEl.textContent = popped + ' popped';
    band.classList.remove('is-lock');
    document.dispatchEvent(new CustomEvent('agb:bday-pop', { detail: { count: popped } }));
    var slot = b._slot, total = b._total;
    // Recorded BEFORE the check: this letter is popped whether or not the sky
    // happens to be empty at this instant.
    markPopped(slot);
    // The Games loader opens a popup set to this signal; the band itself does
    // nothing more.
    checkSky(total);
    // Parked, not scheduled: it comes back with the others once the popping
    // stops. `slot` travels on the element, so the letter returns to its own
    // place in the phrase however long it waits.
    b._slot = slot; b._total = total;
    waiting.push(b);
    scheduleReturn(total);
  }

  /** Bring every waiting balloon back, once nobody has popped for RETURN_MS.
   *
   *  ONE TIMER, restarted by each pop. A timer per balloon counted from the
   *  wrong moment: with fourteen letters in the sky, the first was always back
   *  before the last was gone, so the sky never emptied and the popup this band
   *  exists to fire never fired.
   */
  function scheduleReturn(total) {
    if (returnTimer) clearTimeout(returnTimer);
    returnTimer = setTimeout(function () {
      returnTimer = null;
      var due = waiting;
      waiting = [];
      // OUT OF ORDER, ONE AT A TIME -- the same drift as the band filling at
      // load. All fourteen reappearing on one frame is what made the return
      // read as fast however long the wait before it was: a wall of letters
      // snapping back rather than balloons floating up.
      for (var j = due.length - 1; j > 0; j--) {
        var k = Math.floor(Math.random() * (j + 1));
        var held = due[j]; due[j] = due[k]; due[k] = held;
      }
      due.forEach(function (gone, index) {
        if (!gone.parentNode) return;            // the band was rebuilt under us
        var fresh = makeBalloon(gone._slot, gone._total);
        // It arrives from below, not out of thin air: parked under the band
        // floor, put in place, then released (reading offsetWidth commits the
        // parked position, so removing the class transitions instead of
        // jumping). Its float starts at rest rather than mid-bob, so the rise
        // reads as one continuous climb.
        fresh.style.setProperty('--delay', '0s');
        fresh.classList.add('is-rising');
        gone.replaceWith(fresh);
        void fresh.offsetWidth;
        setTimeout(function () {
          fresh.classList.remove('is-rising');
          checkSky(total);             // this one is back: the sky can empty again
        }, ARRIVE_STEP_MS * index + rnd(0, ARRIVE_STEP_MS));
      });
      checkSky(total);
    }, RETURN_MS);
  }

  // Is this band-relative point on a balloon? Elliptical test on the body, with
  // a little forgiveness, so a click just outside the curve still counts.
  function balloonAt(x, y) {
    var origin = band.getBoundingClientRect();
    var list = sky.querySelectorAll('.agb-bday__balloon:not(.is-popping)');
    for (var i = 0; i < list.length; i++) {
      var r = list[i].firstElementChild.firstElementChild.getBoundingClientRect();
      var cx = r.left - origin.left + r.width / 2, cy = r.top - origin.top + r.height / 2;
      var rx = r.width / 2 + 3, ry = r.height / 2 + 3;
      var dx = (x - cx) / rx, dy = (y - cy) / ry;
      if (dx * dx + dy * dy <= 1) return list[i];
    }
    return null;
  }

  // Every balloon looks at the pointer: near ones lean away from it (a
  // transform on the inner box, so the float animation keeps running); the one
  // under the pointer holds still and lights its target up. Nothing pops here -
  // that takes a click.
  function aimAt(x, y) {
    var origin = band.getBoundingClientRect();
    var list = sky.querySelectorAll('.agb-bday__balloon:not(.is-popping)');
    var locked = null;
    for (var i = 0; i < list.length; i++) {
      var b = list[i], inner = b.firstElementChild, body = inner.firstElementChild;
      var r = body.getBoundingClientRect();
      var cx = r.left - origin.left + r.width / 2, cy = r.top - origin.top + r.height / 2;
      var dx = cx - x, dy = cy - y, d = Math.sqrt(dx * dx + dy * dy) || 0.001;
      var rx = r.width / 2 + 3, ry = r.height / 2 + 3;
      var under = (dx / rx) * (dx / rx) + (dy / ry) * (dy / ry) <= 1;
      b.classList.toggle('is-lock', under);
      if (under) { locked = b; continue; }
      if (d < REPEL_RADIUS) {
        var push = (REPEL_RADIUS - d) / REPEL_RADIUS * repelMax;
        inner.style.setProperty('--rx', (dx / d * push).toFixed(1) + 'px');
        inner.style.setProperty('--ry', (dy / d * push * 0.6).toFixed(1) + 'px');
      } else if (inner.style.getPropertyValue('--rx')) {
        inner.style.setProperty('--rx', '0px'); inner.style.setProperty('--ry', '0px');
      }
    }
    band.classList.toggle('is-lock', !!locked);
  }
  function relax() {
    var list = sky.querySelectorAll('.agb-bday__inner');
    for (var i = 0; i < list.length; i++) { list[i].style.setProperty('--rx', '0px'); list[i].style.setProperty('--ry', '0px'); }
    var locked = sky.querySelectorAll('.agb-bday__balloon.is-lock');
    for (var k = 0; k < locked.length; k++) locked[k].classList.remove('is-lock');
    band.classList.remove('is-lock');
  }
  function bandPoint(evt) {
    var o = band.getBoundingClientRect();
    return { x: evt.clientX - o.left, y: evt.clientY - o.top };
  }
  function overButton(evt) {
    return !!(evt.target && evt.target.closest && evt.target.closest('.agb-bday__btn'));
  }

  // ------------------------------------------------------------ the logo --
  // While the band is up, the header lockup steps aside for the birthday
  // patch. The theme's own <img> is hidden, not removed, so restoring is a
  // class flip. The patch is sized from the header as it stands BEFORE the
  // swap, so the header does not grow.
  var logoHost = null, logoImg = null;
  function swapLogo() {
    var host = document.querySelector('.header__logo');
    if (!host || logoImg) return;
    var target = host.querySelector('a') || host;
    var theirs = host.querySelector('.header__logo-image');
    var header = host.closest('x-header, .header') || host.parentElement;
    var headerH = header ? header.getBoundingClientRect().height : 0;
    var theirsH = theirs ? theirs.getBoundingClientRect().height : 0;
    var h = Math.min(theirsH || 64, headerH ? headerH - 12 : 64);
    h = clamp(Math.round(h), 36, 96);
    logoImg = el('img', 'agb-bday-logo');
    logoImg.src = LOGO_URL;
    logoImg.alt = 'Agilite';
    logoImg.decoding = 'async';
    logoImg.style.height = h + 'px';
    host.classList.add('agb-bday-logo-on');
    target.appendChild(logoImg);
    logoHost = host;
  }
  function restoreLogo() {
    if (logoImg) { logoImg.remove(); logoImg = null; }
    if (logoHost) { logoHost.classList.remove('agb-bday-logo-on'); logoHost = null; }
  }

  // the announcement bar joins in while the band is up (class flip, nothing else)
  var bannerEl = null;
  function dressBanner() {
    bannerEl = document.querySelector('[data-announcement-bar], .announcement-bar');
    if (bannerEl) bannerEl.classList.add('agb-bday-banner');
  }
  function undressBanner() {
    if (bannerEl) { bannerEl.classList.remove('agb-bday-banner'); bannerEl = null; }
  }

  function mount() {
    if (band || !document.body) return;
    // ONE BAND AT A TIME, AND THE ONE THAT GOES UP CLEARS THE OTHER (Dorian,
    // 2026-09-17: "if one is activating itself so it removes the previous one,
    // so there is no competition"). Both flags are set by hand on a preview
    // theme and a forgotten one used to sit there for days before surfacing as
    // "the birthday link is broken". Taking the page also takes the flag, so
    // the state can never be ambiguous afterwards. Costs nothing on a live
    // theme, where the range cannot exist at all.
    try { if (localStorage.getItem('turkey_layout') === 'true') localStorage.setItem('turkey_layout', 'false'); } catch (e) {}
    try { if (window.AGB_TurkeyLayout) window.AGB_TurkeyLayout.hide(); } catch (e) {}
    var style = el('style', null, CSS); style.id = 'agb-bday-css';
    document.head.appendChild(style);

    band = el('div', 'agb-bday'); band.id = 'agb-bday';
    band.setAttribute('role', 'region'); band.setAttribute('aria-label', 'Birthday celebration');
    band.appendChild(el('div', 'agb-bday__bunting'));

    sky = el('div', 'agb-bday__sky');
    var n = balloonCount();
    // THE SKY FILLS IN, IT DOES NOT ARRIVE. Every balloon is in the DOM from
    // the first frame, parked below the floor exactly where a popped one waits
    // -- so the band counts all of them from the start. Balloons that did not
    // exist yet would read to `checkSky` as balloons already gone, and popping
    // the one that had arrived would have emptied the sky and fired the popup
    // after a single pop. A parked balloon's box sits outside the band, so
    // `balloonAt` cannot hit one before it is up.
    var arriving = [];
    for (var i = 0; i < n; i++) {
      var born = makeBalloon(i, n);
      born.classList.add('is-rising');
      sky.appendChild(born);
      arriving.push(born);
    }
    // OUT OF ORDER, the way it is taken apart. Fisher-Yates over the letters,
    // so the phrase assembles in a different order every time.
    for (var j = arriving.length - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var held = arriving[j]; arriving[j] = arriving[k]; arriving[k] = held;
    }
    arriving.forEach(function (balloon, index) {
      // Its own float delay is left alone: `makeBalloon` scattered it on
      // purpose, and zeroing all fourteen would set the whole phrase bobbing
      // in unison.
      setTimeout(function () {
        if (!balloon.parentNode) return;
        void balloon.offsetWidth;          // commit the parked position first
        balloon.classList.remove('is-rising');
      }, ARRIVE_STEP_MS * index + rnd(0, ARRIVE_STEP_MS));
    });
    band.appendChild(sky);

    scoreEl = el('div', 'agb-bday__score', '0 popped');
    band.appendChild(scoreEl);

    /* ONE BUTTON, THREE POSITIONS, in the order a shopper turns it down:
       the tune, then just the pops, then quiet. Each has its own glyph rather
       than one glyph at two opacities - dimming alone cannot say which of
       three a button is in. The label says it in words for anyone who cannot
       see the glyph, and it is re-read on every press. */
    var FACE = {
      music: { glyph: '&#9835;', say: 'Sound: pops and the tune. Press for pops only.' },
      pop:   { glyph: '&#10033;', say: 'Sound: pops only. Press for silence.' },
      off:   { glyph: '&#9835;', say: 'Sound: off. Press for pops and the tune.' }
    };
    var sound = el('button', 'agb-bday__btn agb-bday__sound', FACE[soundMode()].glyph);
    sound.type = 'button';
    function paintSound() {
      var m = soundMode();
      sound.innerHTML = FACE[m].glyph;
      sound.setAttribute('aria-label', FACE[m].say);
      sound.setAttribute('title', FACE[m].say);
      sound.classList.toggle('is-off', m === 'off');
      sound.classList.toggle('is-pop', m === 'pop');
    }
    paintSound();
    sound.addEventListener('click', function (e) {
      e.stopPropagation();
      var next = MODES[(MODES.indexOf(soundMode()) + 1) % MODES.length];
      try { localStorage.setItem(SOUND_KEY, next); } catch (x) {}
      paintSound();
    });
    band.appendChild(sound);

    var close = el('button', 'agb-bday__btn agb-bday__close', '&times;');
    close.type = 'button'; close.setAttribute('aria-label', 'Close the birthday band');
    close.addEventListener('click', function (e) {
      e.stopPropagation();
      try { localStorage.setItem(KEY, 'false'); } catch (x) {}
      unmount();
    });
    band.appendChild(close);

    // The balloons get out of a pointer's way. A mouse only has to hover; a
    // finger has to be down, and while it slides they shy away from it exactly
    // as they do for the mouse (Dorian, 2026-09-16: "on mobile if sliding the
    // fingers on ballons it should push it a bit like the mouse does on
    // hovering on desktop"). That is why a finger no longer pops on the way
    // DOWN - a slide that starts on a balloon has to be able to push it. The
    // pop waits for the release, and only if the finger stayed put. The band
    // takes touch-action:pan-y, so the page still scrolls under a vertical
    // swipe while a sideways slide belongs to the balloons.
    var TAP_SLOP = 10;          // px of travel still counted as a tap, not a slide
    var touching = null;        // { sx, sy, moved } while a finger is down

    function follow(x, y) {
      px = x; py = y;
      if (aimRaf) return;
      aimRaf = requestAnimationFrame(function () { aimRaf = 0; if (band) aimAt(px, py); });
    }

    band.addEventListener('pointerleave', function (evt) {
      if (evt.pointerType === 'touch') return;
      relax();
    });
    band.addEventListener('pointermove', function (evt) {
      var p = bandPoint(evt);
      if (evt.pointerType === 'touch') {
        if (!touching) return;                       // a finger only counts while it is down
        touching.moved = Math.max(touching.moved,
          Math.abs(evt.clientX - touching.sx) + Math.abs(evt.clientY - touching.sy));
      }
      follow(p.x, p.y);
    });
    // a click pops the balloon under the pointer at once; a finger pushes first
    band.addEventListener('pointerdown', function (evt) {
      if (overButton(evt)) return;
      var p = bandPoint(evt);
      if (evt.pointerType === 'touch') {
        touching = { sx: evt.clientX, sy: evt.clientY, moved: 0 };
        follow(p.x, p.y);
        return;
      }
      var b = balloonAt(p.x, p.y);
      if (!b) return;
      evt.preventDefault();
      popBalloon(b, p.x, p.y);
    });
    band.addEventListener('pointerup', function (evt) {
      if (evt.pointerType !== 'touch' || !touching) return;
      var tap = touching.moved <= TAP_SLOP;
      touching = null;
      if (tap) {
        var p = bandPoint(evt);
        var b = balloonAt(p.x, p.y);
        if (b) popBalloon(b, p.x, p.y);
      }
      relax();
    });
    band.addEventListener('pointercancel', function (evt) {
      if (evt.pointerType !== 'touch') return;
      touching = null;
      relax();
    });

    var at = mountPoint();
    at.node.insertAdjacentElement(at.where, band);
    swapLogo();
    dressBanner();
  }

  function unmount() {
    if (!band) return;
    restoreLogo();
    undressBanner();
    band.remove(); band = null; sky = null; scoreEl = null;
    var s = document.getElementById('agb-bday-css'); if (s) s.remove();
  }

  // ----------------------------------------------------------------- boot --
  // For the console and for anything else on the page: show/hide on demand,
  // and how many balloons have gone so far.
  window.AGB_BirthdayOverlay = {
    show: mount,
    hide: unmount,
    armed: armed,
    popped: function () { return popped; },
    total: balloonCount,
    allPopped: function () { return allPopped; }
  };

  function boot() { if (armed()) mount(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Another tab flipped the switch: follow it without a reload.
  window.addEventListener('storage', function (evt) {
    if (evt.key !== KEY) return;
    if (evt.newValue === 'true') mount(); else unmount();
  });
})();
